<template>
    <app-layout>
        <template #page-content>
            <div id="sl-topo__files" class="py-4">
                <!-- Loader -->
                <div v-if="loading" class="py-4">
                    <img src="/img/loader-761.svg" alt="Loading…" class="mx-auto">
                </div>

                <!-- Loading Error -->
                <div v-else-if="loadingError" class="relative rounded p-4 bg-red-600 text-white">
                    <div class="cursor-pointer absolute top-0 right-2 text-3xl" title="Dismiss" @click="setError(null)">
                        &times;
                    </div>

                    <div class="mb-2 font-bold">
                        {{ $t('common.errors.error') }}
                        {{ loadingError.code }}
                    </div>

                    <div>
                        {{ loadingError.message }}
                    </div>
                </div>

                <!-- Page Content -->
                <template v-else>
                    <!-- //////////////////////////////////////////////////////////// Files Listing -->

                    <h2>
                        {{ $t('form.your_files.heading', files.length) }}
                    </h2>

                    <div class="grid grid-cols-1 md:grid-cols-2 mb-8 gap-8">
                        <div>
                            <template v-if="files.length">
                                <p class="whitespace-pre-line">
                                    {{ $t('form.your_files.intro') }}
                                </p>
                            </template>

                            <p v-else>
                                {{ $t('form.your_files.no_files') }}
                            </p>
                        </div>

                        <!-- List files -->
                        <div>
                            <div class="files-list">
                                <div
                                    v-for="file in files"
                                    :key="file.id"
                                    class="files-list__file"
                                >
                                    <div>
                                        {{ $t('form.FILE.uploaded_on_date') }}
                                        <span class="text-primary-500">{{ formatDate(file.created_at) }}</span>
                                        {{ $t('form.FILE.uploaded_at_time') }}
                                        <span class="text-primary-500">{{ formatTime(file.created_at) }}</span>
                                    </div>

                                    <div class="text-gray-400 ml-7 sm:ml-auto sm:mr-0 md:ml-7 md:mr-auto lg:ml-auto lg:mr-0">
                                        {{ formatSize(file.size) }}
                                    </div>

                                    <mdi-icon
                                        :icon="icons.mdiTrashCanOutline"
                                        title="Delete permanently ⚠️"
                                        @click="btnDeleteClick(file)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>


                    <!-- //////////////////////////////////////////////////////////// ORT Form -->

                    <div v-if="files.length">
                        <h2>{{ $t('form.send_files.heading', files.length) }}</h2>

                        <div>
                            <p>{{ $t('form.send_files.intro') }}</p>

                            <div class="examination-form">
                                <!-- Inquiry or Order -->
                                <div class="examination-form__field examination-form__field--full-width mt-0">
                                    <label class="examination-form__label">
                                        {{ $t('form.REQUEST_TYPE.label', files.length) }}

                                        <span class="text-red-400">*</span>
                                    </label>

                                    <topo-radio
                                        v-model="requestType"
                                        :options="requestTypeOptions"
                                    />
                                </div>

                                <!-- New Order or Reorder -->
                                <div class="examination-form__field examination-form__field--full-width">
                                    <label class="examination-form__label">
                                        {{ $t('form.ORDER_TYPE.label') }}
                                    </label>

                                    <topo-radio
                                        v-model="orderType"
                                        :options="orderTypeOptions"
                                    />
                                </div>

                                <!-- Patient Name -->
                                <div class="examination-form__field examination-form__field--full-width">
                                    <label for="patient_name" class="examination-form__label">
                                        {{ $t('form.PATIENT_NAME.label') }}

                                        <span class="text-red-400">*</span>
                                    </label>

                                    <input
                                        id="patient_name"
                                        v-model="patientName"
                                        type="text"
                                        class="examination-form__input"
                                    >
                                </div>

                                <!-- Patient Age -->
                                <div class="examination-form__field examination-form__field--number examination-form__field--full-width">
                                    <label for="patient_age" class="examination-form__label">
                                        {{ $t('form.PATIENT_AGE.label') }}
                                    </label>

                                    <input
                                        id="patient_age"
                                        v-model="patientAge"
                                        type="number"
                                        :min="0"
                                        class="examination-form__input"
                                    >
                                </div>

                                <!-- External Reference -->
                                <div v-if="requestType === 'ORDER'" class="examination-form__field examination-form__field--full-width">
                                    <label for="external_reference" class="examination-form__label">
                                        {{ $t('form.EXT_REF.label') }}
                                    </label>

                                    <input
                                        id="external_reference"
                                        v-model="orderExtRef"
                                        type="text"
                                        class="examination-form__input"
                                        maxlength="18"
                                    >

                                    <div class="examination-form__field-help">
                                        {{ $t('form.EXT_REF.help') }}
                                    </div>
                                </div>


                                <!-- //////////////////////////////////////// Eye -->

                                <div class="grid grid-cols-2 gap-8">
                                    <!-- Form for Right Lens -->
                                    <div>
                                        <div class="examination-form__sub-heading">
                                            {{ capitalizeFirstLetter($t('common.right-eye')) }}
                                        </div>

                                        <eye-form
                                            v-model="rightEyeForm"
                                            :is-reorder="orderType === 'REORDER'"
                                            class="right"
                                        />
                                    </div>

                                    <!-- Form for Left Lens -->
                                    <div>
                                        <div class="examination-form__sub-heading">
                                            {{ capitalizeFirstLetter($t('common.left-eye')) }}
                                        </div>

                                        <eye-form
                                            v-model="leftEyeForm"
                                            :is-reorder="orderType === 'REORDER'"
                                            class="left"
                                        />
                                    </div>
                                </div>


                                <!-- //////////////////////////////////////// Urgent -->

                                <!-- Urgent -->
                                <div v-if="requestType === 'ORDER'" class="examination-form__field examination-form__field--full-width">
                                    <label for="urgent" class="examination-form__label">
                                        {{ $t('form.URGENT.label') }}
                                    </label>

                                    <topo-radio
                                        v-model="urgent"
                                        :options="urgentOptions"
                                    />

                                    <p v-if="urgent" class="examination-form__field-help text-yellow-500">
                                        {{ $t('form.URGENT.help') }}
                                    </p>
                                </div>


                                <!-- //////////////////////////////////////// Submit Form -->

                                <div class="examination-form__field examination-form__field--full-width mt-4">
                                    <button class="topo-button" :disabled="!canSubmit" @click="sendFiles()">
                                        {{ capitalize($t('common.submit')) }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </template>
    </app-layout>
</template>

<script>
import axios from '@/axios';
import Numbers from '@/utils/Numbers';
import Strings from '@/utils/Strings';
import { format } from 'date-fns';
import { mdiTrashCanOutline } from '@mdi/js';
import AppLayout from '@/layouts/AppLayout.vue';
import MdiIcon from '@/components/MdiIcon.vue';
import EyeForm from '@/components/eye-form/EyeForm.vue';

export default
{
    name: 'FilesList',

    components:
    {
        AppLayout,
        MdiIcon,
        EyeForm,
    },

    props:
    {
    },

    data()
    {
        return {
            loading:      true,
            files:        [],
            loadingError: null,
            requestType:  null,
            orderType:    null,
            patientName:  '',
            patientAge:   null,
            orderExtRef:  '',
            rightEyeForm: {},
            leftEyeForm:  {},
            urgent:       null,
        };
    },

    computed:
    {
        topographerId()
        {
            return this.$route.params.topographerId;
        },

        patientId()
        {
            return this.$route.params.patientId;
        },

        lang()
        {
            return new URLSearchParams(window.location.search).get('lang');
        },

        icons()
        {
            return {
                mdiTrashCanOutline,
            };
        },

        requestTypeOptions()
        {

            return [
                {
                    label: this.$t('form.REQUEST_TYPE.values.INQUIRY.label'),
                    value: 'INQUIRY',
                },
                {
                    label: this.$t('form.REQUEST_TYPE.values.ORDER.label'),
                    value: 'ORDER',
                },
            ];
        },

        orderTypeOptions()
        {
            return [
                {
                    label: this.$t('form.ORDER_TYPE.values.NEW_ORDER.label'),
                    value: 'NEW_ORDER',
                },
                {
                    label: this.$t('form.ORDER_TYPE.values.REORDER.label'),
                    value: 'REORDER',
                },
            ];
        },

        urgentOptions()
        {
            return [
                {
                    label: this.$t('common.normal'),
                    value: false,
                },
                {
                    label: this.$t('common.urgent'),
                    value: true,
                },
            ];
        },

        canSubmit()
        {
            return !!(
                this.requestType && // request type required
                this.patientName.trim() && // patient name required
                (this.leftEyeForm.lens_type || this.rightEyeForm.lens_type)
            );
        },
    },

    created()
    {
        this.fetchFiles()
            .catch(this.setError);
    },

    methods:
    {
        setError(error)
        {
            if (error === null)
            {
                // Clear error
                this.loadingError = null;
                return;
            }

            if (!error.response?.errors)
            {
                this.loadingError = {
                    code:    '',
                    message: error.message,
                };
            }
            else
            {
                this.loadingError = {
                    code:    error.response.errors.code,
                    message: error.response.errors.message,
                };
            }
        },

        fetchFiles()
        {
            return new Promise((resolve, reject) =>
            {
                // Prepare localized URL
                let url = `/api/file/topographer/${this.topographerId}/patient/${this.patientId}`;
                if (this.lang)
                {
                    url += '?lang=' + this.lang;
                }

                this.loading = true;
                axios.get(url)
                    .then(({ data: files }) =>
                    {
                        this.files = files;
                        resolve(files);
                    })
                    .catch(error =>
                    {
                        reject(error);
                    })
                    .then(() =>
                    {
                        this.loading = false;
                    });
            });
        },

        formatDate(isoDate)
        {
            return format(new Date(isoDate), 'd MMM yyyy');
        },

        formatTime(isoDate)
        {
            return format(new Date(isoDate), 'HH:mm:ss');
        },

        formatSize(size)
        {
            const G = 1024 * 1024 * 1024;
            const M = 1024 * 1024;
            const K = 1024;

            let unit         = '';
            let computedSize = size;

            switch (true)
            {
                case size >= G:
                    computedSize /= G;
                    unit = 'G';
                    break;

                case size >= M:
                    computedSize /= M;
                    unit = 'M';
                    break;

                case size >= K:
                    computedSize /= K;
                    unit = 'K';
                    break;
            }
            computedSize = Numbers.thousands(computedSize.toFixed(unit === '' ? 0 : 2), ' ');

            return computedSize + ' ' + unit + 'B';
        },

        btnDeleteClick(file)
        {
            const date = this.formatDate(file.created_at);
            const time = this.formatTime(file.created_at);
            const size = this.formatSize(file.size);

            if (confirm(this.$t('file.delete.prompt', { size, date, time })))
            {
                this.deleteFile(file.id)
                    .catch(this.setError);
            }
        },

        deleteFile(fileId)
        {
            return new Promise((resolve, reject) =>
            {
                // Validate param
                const index = this.files.findIndex(f => f.id === fileId);
                if (index === -1)
                {
                    reject('File not found');
                    return;
                }

                // Prepare localized URL
                let url = `/api/file/${fileId}/topographer/${this.topographerId}/patient/${this.patientId}`;
                if (this.lang)
                {
                    url += '?lang=' + this.lang;
                }

                this.loading = true;
                axios.delete(url)
                    .then(response =>
                    {
                        this.files.splice(index, 1);
                        resolve(response.data);
                    })
                    .catch(error =>
                    {
                        reject(error);
                    })
                    .then(() =>
                    {
                        this.loading = false;
                    });
            });
        },

        sendFiles()
        {
            return new Promise((resolve, reject) =>
            {
                if (!this.canSubmit)
                {
                    reject('Submit disabled');
                    return;
                }

                // Build payload with form data
                const data = {
                    request_type: this.requestType,
                    order_type:   this.orderType,
                    patient_name: this.patientName,
                    patient_age:  this.patientAge,
                    ext_ref:      this.orderExtRef,
                    right_eye:    this.rightEyeForm,
                    left_eye:     this.leftEyeForm,
                    urgent:       this.urgent,
                };

                // Send form
                this.loading = true;
                axios.post(`/api/ort/topographer/${this.topographerId}/patient/${this.patientId}`, data)
                    .then(response =>
                    {
                        this.files = [];
                        this.$router.push({
                            name:   'files-sent',
                            params: {
                                requestType: this.requestType.toLowerCase(),
                            },
                        });

                        resolve();
                    })
                    .catch(error =>
                    {
                        this.setError(error);
                        reject(error);
                    })
                    .then(() =>
                    {
                        this.loading = false;
                    });
            });
        },

        capitalize(str = '')
        {
            return Strings.capitalize(str);
        },

        capitalizeFirstLetter(str = '')
        {
            return Strings.capitalizeFirstLetter(str);
        },
    },
};
</script>

<style lang="css" scoped>
@reference "@/assets/styles/main.css";

.files-list
{
    @apply rounded-md shadow-md backdrop-blur-xs;
}

.files-list__file
{
    @apply grid items-center p-2;

    grid-template-columns: auto 100px 30px;
    line-height: 1.31;
}

.files-list__file .mdi-icon
{
    @apply cursor-pointer flex justify-end w-full h-5 text-red-500;
}
</style>
